import { defineStore } from 'pinia';
import { until } from '@vueuse/core';
import { useQueryClient } from '@tanstack/vue-query';
import type { Organisation } from '@@/packages/shared/types/models/organisation';
import { useCurrentOrg } from '@/modules/current-org/api';
import useHasPermissions from '@/modules/current-user/permissions';
import {
  normalizeUserPermissions,
  type UserPermissionsResponse,
} from '@/modules/current-user/api/permissions';
import { CURRENT_USER_PERMISSIONS_QUERY } from '@/modules/current-user/api/queryKeys';
import { getModeLocalStorage, setModeLocalStorage } from './mode';
import { resolveModeForOrg, toModePermissions, type ModePermissions } from './modeAvailability';
import { assertIsMode, type Mode } from './types';

const useModeStore = defineStore('mode', () => {
  // null = not set, '' = set but has no mode (new user)
  const currentMode = ref<null | Mode | ''>(null);

  const queryClient = useQueryClient();
  const { currentOrg } = useCurrentOrg();
  const { isReady: permissionsReady, isError: permissionsError } = useHasPermissions();

  function setMode(mode: Mode) {
    setModeLocalStorage(mode);
    currentMode.value = mode;
  }

  // Read the mode permissions straight from the query cache rather than the
  // reactive `useHasPermissions` refs. During an org switch the shell isn't
  // remounted, so those refs can still hold the previous org's permissions after
  // the cache is cleared (the same staleness AppContent.vue works around by
  // remounting). The cache is authoritative: it holds the target org's
  // permissions the moment the refetch resolves, so resolving from it stops a
  // stored preference stranding the user in a mode they can't access here.
  function currentModePermissions(): ModePermissions {
    const data = queryClient.getQueryData<UserPermissionsResponse>([
      CURRENT_USER_PERMISSIONS_QUERY,
    ]);
    return toModePermissions(normalizeUserPermissions(data));
  }

  // `userMode` is one key shared across orgs, so a preference set elsewhere must
  // not resolve the user into a mode they lack permission for here. resolveModeForOrg
  // gates the stored preference on the org's modes and the user's permissions,
  // read fresh from the cache for the current org (see currentModePermissions).
  function setModeFromOrg(org: Organisation) {
    setMode(resolveModeForOrg(org, currentModePermissions(), getModeLocalStorage()));
  }

  // Derive once permissions have settled (loaded or errored): they gate whether
  // a stored preference is honoured, so deriving earlier would resolve — and
  // navigate on — the wrong mode.
  watch(
    [currentOrg, permissionsReady, permissionsError],
    ([org, ready, errored]) => {
      if (!org) return;
      if (ready) {
        setModeFromOrg(org);
      } else if (errored) {
        // Permissions failed to load, so the cache is empty and resolveModeForOrg
        // sees no permission for any mode: it can't honour the stored preference
        // and falls back to the org's primary type. Derive it anyway so navigation
        // isn't wedged, but do NOT persist — writing that fallback back via setMode
        // would clobber the user's saved preference on a transient fetch failure.
        currentMode.value = resolveModeForOrg(org, currentModePermissions(), getModeLocalStorage());
      }
    },
    { immediate: true },
  );

  return { currentMode, setMode, setModeFromOrg };
});

/**
 * Wraps the pinia store to return a destructurable composable.
 */
export function useMode() {
  const modeStore = useModeStore();

  const currentMode = computed(() => modeStore.currentMode);
  const currentClientOrSupplierMode = computed(() => {
    if (currentMode.value === 'client' || currentMode.value === 'supplier') {
      return currentMode.value;
    }
    throw new Error('Mode is not client or supplier');
  });

  function setMode(mode: string) {
    assertIsMode(mode);
    modeStore.setMode(mode);
  }

  async function resolvedMode() {
    if (currentMode.value === null) {
      await until(currentMode).toMatch((c) => typeof c === 'string');
    }
    if (currentMode.value === null) throw new Error('this is impossible');
    return currentMode.value;
  }

  return {
    currentMode,
    resolvedMode,
    setMode,
    setModeFromOrg: modeStore.setModeFromOrg,
    currentClientOrSupplierMode,
  };
}

export function useIsRestrictedOrg() {
  const { currentOrg } = useCurrentOrg();
  const isRestrictedOrg = computed(() => !!currentOrg.value?.isClientRestricted);

  return { isRestrictedOrg };
}
